import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';

import { planningsApi } from '../axios/plannings';
import { taskAssignsApi } from '../axios/taskAssigns';
import { useLanguage } from '../hooks';
import {
  AssetByMeter,
  CancelTaskAssignPayload,
  CreateMeterStrategyPayload,
  CreatePlanningPayload,
  CreatePlanningTaskAssignPayload,
  GroupedByAssetParams,
  GroupedByAssetResponse,
  ImportAuditDetail,
  ImportAuditSummary,
  ListPlanningsParams,
  ListPlanningsResponse,
  ListTaskAssignsParams,
  ListTaskAssignsResponse,
  MeterStrategy,
  PlanningChangeLog,
  PlanningDetail,
  PlanningType,
  PlanningTypePayload,
  ProjectionPreview,
  ProjectionPreviewParams,
  ReassignTaskAssignPayload,
  StartImportResponse,
  UpdatePlanningPayload,
} from '../types/plannings';

/**
 * Centralized query keys. Always derive nested keys from `planningKeys.all` so
 * invalidations cascade correctly.
 */
export const planningKeys = {
  all: ['plannings'] as const,
  lists: () => [...planningKeys.all, 'list'] as const,
  list: (params: ListPlanningsParams) => [...planningKeys.lists(), params] as const,
  details: () => [...planningKeys.all, 'detail'] as const,
  // `withTasks` differentiates the "full" detail (wizard) from the "light"
  // one (detail view), because the backend responds with different shapes
  // depending on `include_task_assigns`. Without it react-query would mix caches.
  detail: (id: number, withTasks = true) =>
    [...planningKeys.details(), id, { withTasks }] as const,
  // Prefix to invalidate ALL variants (`withTasks: true|false`) of a
  // planning. Use in mutations instead of `detail(id)`, which points only
  // to the embedded-tasks variant and would leave the light version stale.
  detailFor: (id: number) => [...planningKeys.details(), id] as const,
  taskAssigns: (planningId: number) => [...planningKeys.all, 'task_assigns', planningId] as const,
  taskAssignsList: (params: ListTaskAssignsParams) =>
    [...planningKeys.taskAssigns(params.planning_id), params] as const,
  // Nest types and importAudits under `all` so that a
  // `queryClient.invalidateQueries({ queryKey: planningKeys.all })`
  // (e.g. on account switch or logout) cascades to every cache in the
  // module. Keeping them as separate roots left those caches stale
  // after a global reset.
  types: () => [...planningKeys.all, 'types'] as const,
  importAudits: () => [...planningKeys.all, 'import_audits'] as const,
  importAudit: (id: number) => [...planningKeys.importAudits(), id] as const,
  // Meter planning (W1). Nullable args so disabled hooks get a stable, distinct
  // key (TanStack hashes keys structurally) instead of a `0`/`{}` sentinel that
  // could collide with a real query.
  meterStrategies: () => [...planningKeys.all, 'meter_strategies'] as const,
  assetsByMeter: (meterId: number | null) =>
    [...planningKeys.all, 'assets_by_meter', meterId] as const,
  projectionPreview: (params: ProjectionPreviewParams | null) =>
    [...planningKeys.all, 'projection_preview', params] as const,
  groupedByAsset: (params: GroupedByAssetParams) =>
    [...planningKeys.all, 'grouped_by_asset', params] as const,
  // Recent readings card in the meter detail. `assetId` is nullable for the
  // disabled state (planning without asset), matching the pattern above.
  meterReadings: (assetId: number | null, meterId: string) =>
    [...planningKeys.all, 'meter_readings', assetId, meterId] as const,
  // Change history of a planning.
  changeLogs: (planningId: number) => [...planningKeys.all, 'change_logs', planningId] as const,
};

// ---------- Reads ----------

export function useListPlanningsQuery(params: ListPlanningsParams, enabled = true) {
  return useQuery<ListPlanningsResponse>({
    queryKey: planningKeys.list(params),
    queryFn: () => planningsApi.listPlannings(params),
    // DS-4043: lets the Planificaciones block hold off the request until it
    // scrolls into view (useLazyBlock, AC-16). Defaulted true so every
    // existing caller behaves exactly as before.
    enabled,
  });
}

/** The Assets View (grouped-by-asset table view). Same params/filters as
 *  {@link useListPlanningsQuery} — only the response shape differs. */
export function useGroupedByAssetQuery(params: GroupedByAssetParams, enabled = true) {
  return useQuery<GroupedByAssetResponse>({
    queryKey: planningKeys.groupedByAsset(params),
    queryFn: () => planningsApi.groupedByAsset(params),
    enabled,
  });
}

/**
 * Loads the Planning detail. By default it brings the embedded `task_assigns`
 * array (the edit wizard depends on it). The detail view passes
 * `{ includeTaskAssigns: false }` and fetches the tasks paginated per cycle
 * via `useTaskAssignsQuery`.
 */
export function usePlanningQuery(
  id: number | null | undefined,
  options: { includeTaskAssigns?: boolean } = {},
) {
  const includeTaskAssigns = options.includeTaskAssigns ?? true;
  return useQuery<PlanningDetail>({
    queryKey: planningKeys.detail(id ?? 0, includeTaskAssigns),
    queryFn: () => planningsApi.getPlanning(id as number, { includeTaskAssigns }),
    enabled: !!id,
  });
}

/**
 * Paginated list of TaskAssigns for a Planning cycle. Server-side it is
 * filtered by `cycle`, `mobile_user_ids` and `search`; `acceptance_status` is
 * computed in Ruby pre-pagination to keep `total`/`has_more` exact.
 *
 * We use `keepPreviousData` (via `placeholderData`) so that when switching
 * page or cycle the table keeps the previous content while the new response
 * arrives, instead of showing a spinner that makes it "jump".
 */
export function useTaskAssignsQuery(params: ListTaskAssignsParams, enabled = true) {
  return useQuery<ListTaskAssignsResponse>({
    queryKey: planningKeys.taskAssignsList(params),
    queryFn: () => planningsApi.listTaskAssigns(params),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function usePlanningTypesQuery(options?: { enabled?: boolean }) {
  return useQuery<PlanningType[]>({
    queryKey: planningKeys.types(),
    queryFn: () => planningsApi.listPlanningTypes(),
    enabled: options?.enabled ?? true,
  });
}

export function useImportAuditsQuery() {
  return useQuery<ImportAuditSummary[]>({
    queryKey: planningKeys.importAudits(),
    queryFn: () => planningsApi.listImportAudits(),
  });
}

export function useImportAuditQuery(id: number | null | undefined) {
  return useQuery<ImportAuditDetail>({
    queryKey: planningKeys.importAudit(id ?? 0),
    queryFn: () => planningsApi.getImportAudit(id as number),
    enabled: !!id,
    // Poll every 2s while the import is in-flight. Stops automatically when
    // the worker reaches a terminal state (completed/failed) so we don't keep
    // hitting the API forever. The manual "Refresh" button remains as a
    // belt-and-suspenders fallback if the worker hangs or polling is throttled.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'processing' ? 2000 : false;
    },
  });
}

// ---------- Meter planning reads (W1) ----------

export function useMeterStrategiesQuery(enabled = true) {
  return useQuery<MeterStrategy[]>({
    queryKey: planningKeys.meterStrategies(),
    queryFn: () => planningsApi.listMeterStrategies(),
    enabled,
  });
}

/**
 * Inline strategy authoring (AC-1). On success the meter strategy list is
 * invalidated so the new strategy appears in the selector for this and any other
 * meter type of the same unit.
 */
export function useCreateMeterStrategyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMeterStrategyPayload) => planningsApi.createMeterStrategy(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningKeys.meterStrategies() });
    },
  });
}

/**
 * Soft-delete a meter strategy (AC-2). Invalidates the strategy list so the
 * deleted one drops out of the selector.
 */
export function useDeleteMeterStrategyMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: number) => planningsApi.deleteMeterStrategy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningKeys.meterStrategies() });
    },
    onError: () =>
      enqueueSnackbar(lang.plannings.types.form.meter.strategyEditor.deleteError, { variant: 'error' }),
  });
}

export function useAssetsByMeterQuery(meterId: number | null | undefined) {
  return useQuery<AssetByMeter[]>({
    queryKey: planningKeys.assetsByMeter(meterId ?? null),
    queryFn: () => {
      // `enabled` stops automatic fetches, but a manual refetch or cache path
      // can still invoke queryFn; guard instead of asserting non-null.
      if (!meterId) throw new Error('useAssetsByMeterQuery: meterId is required');
      return planningsApi.assetsByMeter(meterId);
    },
    enabled: !!meterId,
  });
}

/**
 * Live projection preview. `params` is null until the form has the three
 * required ids (meter_strategy_id, asset_id, meter_id); W3 passes a debounced
 * value. `placeholderData` keeps the previous preview visible while a new one
 * loads so the panel does not flicker on every param change (mirrors
 * `useTaskAssignsQuery`).
 */
export function useProjectionPreviewQuery(params: ProjectionPreviewParams | null) {
  const ready =
    !!params &&
    !!params.meter_strategy_id &&
    !!params.asset_id &&
    !!params.meter_id;
  return useQuery<ProjectionPreview>({
    queryKey: planningKeys.projectionPreview(params),
    queryFn: () => {
      // `enabled` guards automatic fetches; a manual refetch or cache path can
      // still call queryFn, so guard instead of asserting non-null.
      if (!ready || !params) {
        throw new Error(
          'useProjectionPreviewQuery: meter_strategy_id, asset_id and meter_id are required',
        );
      }
      return planningsApi.projectionPreview(params);
    },
    enabled: ready,
    // Each param combo is a distinct key, so a cached preview only goes stale
    // when params change (W3 debounces before updating them). An explicit
    // staleTime avoids redundant refetches on remount/refocus for the same key.
    staleTime: 5 * 60 * 1000,
    placeholderData: (previous) => previous,
  });
}

// ---------- Planning writes ----------

export function useCreatePlanningMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePlanningPayload) => planningsApi.createPlanning(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningKeys.lists() });
    },
  });
}

export function useUpdatePlanningMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdatePlanningPayload }) =>
      planningsApi.updatePlanning(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: planningKeys.lists() });
      queryClient.invalidateQueries({ queryKey: planningKeys.detailFor(data.id) });
    },
  });
}

export function useDeletePlanningMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: number) => planningsApi.deletePlanning(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningKeys.lists() });
      enqueueSnackbar(lang.plannings.toasts.deleted, { variant: 'success' });
    },
    onError: () => enqueueSnackbar(lang.plannings.toasts.error, { variant: 'error' }),
  });
}

// Change history of a planning (drives the "Historial de cambios" drawer).
export function usePlanningChangeLogsQuery(id: number | null | undefined, enabled = true) {
  return useQuery<PlanningChangeLog[]>({
    queryKey: planningKeys.changeLogs(id ?? 0),
    queryFn: () => planningsApi.getPlanningChangeLogs(id as number),
    enabled: !!id && enabled,
  });
}

// Add a shared responsible (shared mode). Writes the response straight into the
// detail cache (every `detail(id, withTasks)` variant) instead of only
// invalidating, so a caller that reads `planningDetail.responsibles` right
// after this resolves sees the new row immediately — no window where a second,
// rapid add/remove diffs against a stale responsibles list while the
// invalidated query is still refetching in the background.
export function useAddPlanningResponsibleMutation(planningId: number) {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (target: { mobile_user_id?: number; group_id?: number }) =>
      planningsApi.addPlanningResponsible(planningId, target),
    onSuccess: (data) => {
      queryClient.setQueriesData({ queryKey: planningKeys.detailFor(planningId) }, () => data);
      queryClient.invalidateQueries({ queryKey: planningKeys.changeLogs(planningId) });
    },
    onError: () => enqueueSnackbar(lang.plannings.toasts.error, { variant: 'error' }),
  });
}

// Remove a shared responsible, optionally cancelling their open current-
// cycle tasks. Same synchronous cache write as the add mutation, for the same
// stale-diff reason.
export function useRemovePlanningResponsibleMutation(planningId: number) {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ responsibleId, cancelOpenTasks }: { responsibleId: number; cancelOpenTasks?: boolean }) =>
      planningsApi.removePlanningResponsible(planningId, responsibleId, { cancelOpenTasks }),
    onSuccess: (data) => {
      queryClient.setQueriesData({ queryKey: planningKeys.detailFor(planningId) }, () => data);
      queryClient.invalidateQueries({ queryKey: planningKeys.taskAssigns(planningId) });
      queryClient.invalidateQueries({ queryKey: planningKeys.changeLogs(planningId) });
    },
    onError: () => enqueueSnackbar(lang.plannings.toasts.error, { variant: 'error' }),
  });
}

type LifecycleToast = {
  onSuccess?: (data: PlanningDetail) => void;
  onError?: (err: unknown) => void;
};

function buildLifecycleMutation(
  action: (id: number) => Promise<PlanningDetail>,
  queryClient: ReturnType<typeof useQueryClient>,
  toast?: LifecycleToast,
) {
  return {
    mutationFn: action,
    onSuccess: (data: PlanningDetail) => {
      queryClient.invalidateQueries({ queryKey: planningKeys.lists() });
      queryClient.invalidateQueries({ queryKey: planningKeys.detailFor(data.id) });
      toast?.onSuccess?.(data);
    },
    onError: (err: unknown) => toast?.onError?.(err),
  };
}

export function usePublishPlanningMutation() {
  const queryClient = useQueryClient();
  return useMutation(buildLifecycleMutation(planningsApi.publishPlanning, queryClient));
}

export function usePausePlanningMutation() {
  const queryClient = useQueryClient();
  return useMutation(buildLifecycleMutation(planningsApi.pausePlanning, queryClient));
}

export function useResumePlanningMutation() {
  const queryClient = useQueryClient();
  return useMutation(buildLifecycleMutation(planningsApi.resumePlanning, queryClient));
}

export function useArchivePlanningMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(
    buildLifecycleMutation(planningsApi.archivePlanning, queryClient, {
      onSuccess: (data) =>
        enqueueSnackbar(lang.plannings.toasts.archived.replace('{name}', data.name), {
          variant: 'success',
        }),
      onError: () => enqueueSnackbar(lang.plannings.toasts.error, { variant: 'error' }),
    }),
  );
}

export function useUnarchivePlanningMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(
    buildLifecycleMutation(planningsApi.unarchivePlanning, queryClient, {
      onSuccess: (data) =>
        enqueueSnackbar(lang.plannings.toasts.unarchived.replace('{name}', data.name), {
          variant: 'success',
        }),
      // The backend refuses with a specific code (e.g. `rule_completed` when the
      // schedule is exhausted); surface that reason instead of a generic toast.
      onError: (err) => {
        const code = (err as AxiosError<{ error?: string }>).response?.data?.error;
        const dict = lang.plannings.detail.lifecycleErrors;
        const message =
          code && code in dict ? dict[code as keyof typeof dict] : lang.plannings.toasts.error;
        enqueueSnackbar(message, { variant: 'error' });
      },
    }),
  );
}

// ---------- PlanningType writes ----------

export function useCreatePlanningTypeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PlanningTypePayload) => planningsApi.createPlanningType(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningKeys.types() });
    },
  });
}

export function useUpdatePlanningTypeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: PlanningTypePayload }) =>
      planningsApi.updatePlanningType(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningKeys.types() });
      // A type's flags (allows_edit, shared_responsibles, allows_frequency_edit)
      // are surfaced as derived fields on every planning of that type, so the
      // plannings list/detail must refresh too — otherwise the row "Edit" action
      // keeps showing after the type turns allows_edit off.
      queryClient.invalidateQueries({ queryKey: planningKeys.lists() });
      queryClient.invalidateQueries({ queryKey: planningKeys.details() });
    },
  });
}

export function useDeletePlanningTypeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => planningsApi.deletePlanningType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningKeys.types() });
      // Removing a type changes the editability of its plannings, so refresh
      // the plannings list/detail as well.
      queryClient.invalidateQueries({ queryKey: planningKeys.lists() });
      queryClient.invalidateQueries({ queryKey: planningKeys.details() });
    },
  });
}

// ---------- Import ----------

export function useStartImportMutation() {
  const queryClient = useQueryClient();
  return useMutation<StartImportResponse, Error, File>({
    mutationFn: (file: File) => planningsApi.startImport(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningKeys.importAudits() });
      queryClient.invalidateQueries({ queryKey: planningKeys.lists() });
    },
  });
}

// ---------- TaskAssign actions tied to a Planning ----------

/**
 * Cancel a TaskAssign that belongs to a Planning. Caller passes the parent
 * `planningId` so we can invalidate the right detail cache after success.
 */
export function useCancelTaskAssignMutation(planningId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskAssignId,
      payload,
      isGroup = false,
    }: {
      taskAssignId: number;
      payload: CancelTaskAssignPayload;
      // Group rows carry a GroupTaskAssign id, which the individual cancel
      // endpoint 404s on; route them to the group cancel endpoint instead.
      isGroup?: boolean;
    }) =>
      isGroup
        ? taskAssignsApi.cancelGroupTask(taskAssignId, payload)
        : planningsApi.cancelTaskAssign(taskAssignId, payload),
    onSuccess: () => {
      if (planningId) {
        queryClient.invalidateQueries({ queryKey: planningKeys.detailFor(planningId) });
        queryClient.invalidateQueries({ queryKey: planningKeys.taskAssigns(planningId) });
      }
      queryClient.invalidateQueries({ queryKey: planningKeys.lists() });
    },
  });
}

/**
 * Create a TaskAssign that belongs to a Planning. Used by the wizard's Step 3
 * to fill the closed-form set (one TaskAssign per required form).
 */
export function useCreatePlanningTaskAssignMutation(planningId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePlanningTaskAssignPayload) =>
      planningsApi.createPlanningTaskAssign(payload),
    onSuccess: () => {
      if (planningId) {
        queryClient.invalidateQueries({ queryKey: planningKeys.detailFor(planningId) });
        queryClient.invalidateQueries({ queryKey: planningKeys.taskAssigns(planningId) });
      }
    },
  });
}

export function useReassignTaskAssignMutation(planningId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskAssignId,
      payload,
    }: {
      taskAssignId: number;
      payload: ReassignTaskAssignPayload;
    }) => planningsApi.reassignTaskAssign(taskAssignId, payload),
    onSuccess: () => {
      if (planningId) {
        queryClient.invalidateQueries({ queryKey: planningKeys.detailFor(planningId) });
        queryClient.invalidateQueries({ queryKey: planningKeys.taskAssigns(planningId) });
      }
      // The module list shows the assignee's name in the supervisors
      // column; without this invalidation the list kept the old name
      // until the next manual refresh.
      queryClient.invalidateQueries({ queryKey: planningKeys.lists() });
      // The TaskAssigner module list (outside the planning detail) shows the
      // assignee too, so refresh it as well.
      queryClient.invalidateQueries({ queryKey: ['taskAssignsReact'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['taskAssignsReactBoxes'], exact: false });
    },
  });
}

// Type-preserving reassignment for group rows: moves the cycle's group task to
// a different group. Invalidates the same planning caches as the individual
// reassignment so the detail table reflects the new group immediately.
export function useReassignGroupTaskMutation(planningId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupTaskAssignId, groupId }: { groupTaskAssignId: number; groupId: number }) =>
      planningsApi.reassignGroupTask(groupTaskAssignId, { groupId }),
    onSuccess: () => {
      if (planningId) {
        queryClient.invalidateQueries({ queryKey: planningKeys.detailFor(planningId) });
        queryClient.invalidateQueries({ queryKey: planningKeys.taskAssigns(planningId) });
      }
      queryClient.invalidateQueries({ queryKey: planningKeys.lists() });
      // The TaskAssigner module list shows the group_name; without this it
      // stays stale when the reassign happens outside the planning detail.
      queryClient.invalidateQueries({ queryKey: ['taskAssignsReact'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['taskAssignsReactBoxes'], exact: false });
    },
  });
}
